import { useCallback, useMemo, useRef, useEffect } from 'react';
import throttle from 'lodash/throttle';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import { MAX_ALLOWED_QUEUE_SIZE, QUERY_ISSUE_DETAILS_DELAY_TIME } from '../common/constants.tsx';

import { useSubscribeToIssueEvent } from '../common/utils/issue-pub-sub/index.tsx';
import { ISSUE_EVENT_NAMES } from '../common/utils/issue-pub-sub/types.tsx';

import type { IssueUpdateList } from './types.tsx';
import { useRealtimeUpdateExceededMaxFlag } from './utils.tsx';

const REAL_TIME_EVENT_TYPE_KEY = 'realTimeEventType';

type Props = {
	onIssuesCreatedUpdated: (issues: IssueUpdateList) => void;
	onDelete: (store: RecordSourceSelectorProxy, issueAri: string) => boolean;
	onRefetch?: () => void;
};

export const IssueRealtimeUpdates = ({ onIssuesCreatedUpdated, onDelete, onRefetch }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const throttledFireTrackAnalytics = useMemo(() => throttle(fireTrackAnalytics, 1000), []);
	const hasExceededMaxQueueSizeRef = useRef(false);
	const showRealtimeUpdateExceededMaxFlag = useRealtimeUpdateExceededMaxFlag();

	const queueRef = useRef<Map<string, { issueAri: string; issueId: string }>>(new Map());
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const processQueue = useCallback(() => {
		if (queueRef.current.size === 0 || hasExceededMaxQueueSizeRef.current) {
			return;
		}

		fireOperationalAnalytics(createAnalyticsEvent({}), 'realTimeEventQueueProcessing started', {
			queueSize: queueRef.current.size,
		});

		if (queueRef.current.size > MAX_ALLOWED_QUEUE_SIZE) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'realTimeEventsExceededMaxFlag shown', {
				queueSize: queueRef.current.size,
			});

			hasExceededMaxQueueSizeRef.current = true;
			showRealtimeUpdateExceededMaxFlag({
				onRefresh: () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'realTimeEventsExceededMaxRefresh clicked');
					hasExceededMaxQueueSizeRef.current = false;
					queueRef.current = new Map();
					onRefetch?.();
				},
			});
		} else {
			onIssuesCreatedUpdated(Array.from(queueRef.current.values()));
		}

		queueRef.current = new Map();
	}, [createAnalyticsEvent, showRealtimeUpdateExceededMaxFlag, onRefetch, onIssuesCreatedUpdated]);

	const triggerDelayedProcessingOfQueue = useCallback(() => {
		// Existing timeout exists, so we don't need to create a new one
		if (timeoutRef.current) {
			return;
		}

		timeoutRef.current = setTimeout(() => {
			processQueue();
			timeoutRef.current = null;
		}, QUERY_ISSUE_DETAILS_DELAY_TIME);
	}, [processQueue]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const onUpdatedOrCreated = useCallback((payload: { resourceId: string }) => {
		const { resourceId } = payload;
		const issueId = JiraIssueAri.parse(resourceId).issueId;
		handleCreateOrUpdateEventRef.current(resourceId, issueId);
	}, []);

	const onDeleted = useCallback(
		(payload: { resourceId: string; store: RecordSourceSelectorProxy }) => {
			const { resourceId, store } = payload;
			const wasDeleted = onDeleteRef.current(store, resourceId);
			wasDeleted &&
				throttledFireTrackAnalytics(createAnalyticsEvent({}), 'realTimeEvent processed', {
					[REAL_TIME_EVENT_TYPE_KEY]: 'delete',
				});
		},
		[createAnalyticsEvent, throttledFireTrackAnalytics],
	);

	useSubscribeToIssueEvent(ISSUE_EVENT_NAMES.UPDATED_OR_CREATED, onUpdatedOrCreated);
	useSubscribeToIssueEvent(ISSUE_EVENT_NAMES.DELETED, onDeleted);

	const handleCreateOrUpdateEvent = useCallback(
		(issueAri: string, issueId: string) => {
			queueRef.current.set(issueAri, { issueAri, issueId });

			triggerDelayedProcessingOfQueue();
		},
		[triggerDelayedProcessingOfQueue],
	);
	const handleCreateOrUpdateEventRef = useRef(handleCreateOrUpdateEvent);
	if (handleCreateOrUpdateEventRef.current !== handleCreateOrUpdateEvent) {
		handleCreateOrUpdateEventRef.current = handleCreateOrUpdateEvent;
	}

	const onDeleteRef = useRef(onDelete);
	if (onDeleteRef.current !== onDelete) {
		onDeleteRef.current = onDelete;
	}

	return null;
};
